import axios from 'axios';

const isProduction = process.env.NODE_ENV === 'production';

// ---------------------------------------------------------------------------
// Token storage
// ---------------------------------------------------------------------------
// Store in localStorage with fallback to sessionStorage so PWA / home-screen
// shortcut apps and mobile browser sessions stay persistent across back-navigation,
// tab switches, and app restarts.
const TOKEN_KEY = 'token';

export const tokenStorage = {
  get: () => {
    try {
      return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
    } catch (e) {
      return null;
    }
  },
  set: (token) => {
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch (e) {}
    try {
      sessionStorage.setItem(TOKEN_KEY, token);
    } catch (e) {}
  },
  remove: () => {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch (e) {}
    try {
      sessionStorage.removeItem(TOKEN_KEY);
    } catch (e) {}
  }
};

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000',
  headers: { 'Content-Type': 'application/json' },
  // Hard cap on response size that axios will accept
  maxContentLength: 10 * 1024 * 1024, // 10 MB
  timeout: 30000 // 30 s per request
});

// ---------------------------------------------------------------------------
// Request interceptor — attach token
// ---------------------------------------------------------------------------
api.interceptors.request.use(
  (config) => {
    const token = tokenStorage.get();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ---------------------------------------------------------------------------
// Response interceptor — handle errors
// ---------------------------------------------------------------------------
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      if (error.response.status === 401) {
        tokenStorage.remove();
        delete api.defaults.headers.common['Authorization'];
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
      if (!isProduction) {
        if (error.response.status === 403) {
          console.warn('[api] 403 Forbidden:', error.response.config?.url);
        }
        if (error.response.status === 500) {
          console.warn('[api] 500 Server error:', error.response.config?.url);
        }
      }
    } else if (error.request) {
      if (!isProduction) console.warn('[api] Network error — no response received');
    }
    return Promise.reject(error);
  }
);

// ---------------------------------------------------------------------------
// Init — set default header if token already in storage
// ---------------------------------------------------------------------------
const existingToken = tokenStorage.get();
if (existingToken) {
  api.defaults.headers.common['Authorization'] = `Bearer ${existingToken}`;
}

export default api;
